import { useEffect, useState, useCallback } from "react";
import { Gift, ToggleLeft, ToggleRight, Trash, X, Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * CompsSubTable — mounted at the bottom of super-admin > People and Silos tab.
 * Feb 16 2026 (Randy pt-comp-grant-system) — audit-trail table for every
 * comp grant issued. Filter by status, toggle dues, revoke.
 *
 * Oct 4 2026 (Randy — "where exactly do I go if I want to grant an individual
 * a free creator account… I can't find where it is") — and he was right not to
 * find it. The only way in was a bare 14px gift icon in the last column of the
 * Users table, labelled by a `title` tooltip, and a tooltip does not exist on
 * a phone. He went looking HERE, at the comp-grants table, which is the
 * obvious place and had no way to create one. So the button lives here now,
 * with a word on it, plus a user picker that opens the same modal.
 */
export default function CompsSubTable({ onGrant, refreshKey = 0 }) {
  const [status, setStatus] = useState("active"); // "active" | "expired" | "revoked" | "all"
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [picking, setPicking] = useState(false);
  const [pickQ, setPickQ] = useState("");
  const [pickRows, setPickRows] = useState([]);
  const [pickBusy, setPickBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (status && status !== "all") q.set("status", status);
      if (search.trim()) q.set("search", search.trim());
      q.set("per_page", "100");
      const r = await api.get(`/admin/comp-grants?${q.toString()}`);
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load comp grants");
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  // `refreshKey` is bumped by SuperAdmin after a grant lands, so a new row
  // shows up immediately instead of only after the admin pokes a filter.
  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    if (!picking) return;
    const t = setTimeout(async () => {
      setPickBusy(true);
      try {
        const r = await api.get(`/admin/users?page=1&size=8&q=${encodeURIComponent(pickQ.trim())}`);
        setPickRows(r.data?.users || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to search users");
      } finally {
        setPickBusy(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [picking, pickQ]);

  const toggleDues = async (row) => {
    try {
      const nextWaived = !row.effective_dues_waived;
      await api.patch(`/admin/users/${row.user_id}/comp/${row.id}/dues`, { waived: nextWaived });
      toast.success(nextWaived ? "Dues waived" : "Dues required — user will see the alert");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed");
    }
  };

  const revoke = async () => {
    if (!revokeTarget) return;
    if (!revokeReason.trim()) { toast.error("Revoke reason is required"); return; }
    try {
      await api.delete(`/admin/users/${revokeTarget.user_id}/comp/${revokeTarget.id}`, {
        data: { reason: revokeReason.trim() },
      });
      toast.success(`Revoked comp for ${revokeTarget.user_email}`);
      setRevokeTarget(null);
      setRevokeReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return iso; }
  };

  const badgeForStatus = (s) => {
    const TONES = {
      active: "bg-[#E8EFE9] text-[#3A4F40]",
      expired: "bg-[#F4EEDC] text-[#6B5A2D]",
      revoked: "bg-[#FBE9E1] text-[#7A3A2A]",
    };
    return <span className={`text-xs px-2 py-0.5 rounded ${TONES[s] || "bg-[#F0F1EE] text-[#6B6A68]"}`}>{s}</span>;
  };

  return (
    <section className="mt-12 mb-16" data-testid="comps-sub-table">
      <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Gift size={18} weight="duotone" className="text-[#4A5D4E]" />
          <h2 className="font-serif-display text-2xl">Comp grants</h2>
          <span className="text-xs text-[#6B6A68] ml-2">{total} total</span>
        </div>
        <button type="button" onClick={() => { setPicking(true); setPickQ(""); }}
                data-testid="comps-grant-new"
                className="inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4F40] transition-colors">
          <Plus size={14} weight="bold" /> Grant free access
        </button>
      </div>
      <p className="text-xs text-[#6B6A68] -mt-1 mb-3 max-w-2xl">
        Waives the monthly subscription, gifts Creator Pro (whole bundle or chosen pieces),
        and can add LLM credit — all on one screen. Nothing is charged to the recipient.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {["active", "expired", "revoked", "all"].map((s) => (
          <button key={s} type="button" onClick={() => setStatus(s)}
                  data-testid={`comps-filter-${s}`}
                  className={`text-xs px-3 py-1 rounded-full border hairline ${status === s ? "bg-[#4A5D4E] text-white" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}>
            {s}
          </button>
        ))}
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
               data-testid="comps-search"
               placeholder="Search email or reason…"
               className="ml-auto text-sm px-3 py-1 border hairline rounded-md w-full sm:w-64" />
      </div>

      <div className="bg-white border hairline rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-[#F0F1EE] text-[#6B6A68] text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Reason</th>
              <th className="text-left px-4 py-3">Granted by</th>
              <th className="text-left px-4 py-3">Granted on</th>
              <th className="text-left px-4 py-3">Expires</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Dues</th>
              <th className="text-right px-4 py-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t hairline" data-testid={`comps-row-${r.id}`}>
                <td className="px-4 py-3">{r.user_email}</td>
                <td className="px-4 py-3 text-[#6B6A68]">{r.reason}</td>
                <td className="px-4 py-3 text-[#6B6A68]">{r.granted_by_email}</td>
                <td className="px-4 py-3 text-[#6B6A68]">{fmtDate(r.granted_at)}</td>
                <td className="px-4 py-3 text-[#6B6A68]">{fmtDate(r.expires_at)}</td>
                <td className="px-4 py-3">{badgeForStatus(r.status)}</td>
                <td className="px-4 py-3">
                  {r.status === "active" ? (
                    <span className={`text-xs ${r.effective_dues_waived ? "text-[#3A4F40]" : "text-[#7A3A2A]"}`}>
                      {r.effective_dues_waived ? "waived" : "required"}
                    </span>
                  ) : <span className="text-xs text-[#9A9893]">—</span>}
                </td>
                <td className="px-2 py-3 text-right">
                  {r.status === "active" && (
                    <>
                      <button type="button" onClick={() => toggleDues(r)}
                              data-testid={`comps-toggle-dues-${r.id}`}
                              title={r.effective_dues_waived ? "Currently waived — click to require monthly dues" : "Currently required — click to waive"}
                              className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]">
                        {r.effective_dues_waived ? <ToggleRight size={16} weight="duotone" /> : <ToggleLeft size={16} weight="duotone" />}
                      </button>
                      <button type="button" onClick={() => setRevokeTarget(r)}
                              data-testid={`comps-revoke-${r.id}`}
                              title={`Revoke comp for ${r.user_email}`}
                              className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#FDF1ED]">
                        <Trash size={14} weight="duotone" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#6B6A68] text-sm">
                {loading ? "Loading…" : "No comp grants match."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* User picker — step one of granting, so the admin never has to go
          hunting through the Users table for a gift icon. */}
      {picking && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 pt-[12vh]"
             data-testid="comps-pick-backdrop" onClick={() => setPicking(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b hairline">
              <h3 className="font-serif-display text-lg">Who gets free access?</h3>
              <button type="button" onClick={() => setPicking(false)}
                      data-testid="comps-pick-close"
                      className="p-1 text-[#6B6A68] hover:text-[#1C1B1A]">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="relative">
                <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A9893]" />
                <input type="text" value={pickQ} autoFocus
                       onChange={(e) => setPickQ(e.target.value)}
                       data-testid="comps-pick-search"
                       placeholder="Search by email or name…"
                       className="w-full pl-9 pr-3 py-2 border hairline rounded-md text-sm" />
              </div>
              <div className="mt-3 max-h-[46vh] overflow-y-auto divide-y hairline">
                {pickRows.map((u) => (
                  <button key={u.id} type="button"
                          onClick={() => { setPicking(false); onGrant?.(u); }}
                          data-testid={`comps-pick-${u.email}`}
                          className="w-full text-left px-3 py-2.5 hover:bg-[#F0F1EE] flex items-center justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block text-sm text-[#1C1B1A] truncate">{u.email}</span>
                      {u.name && <span className="block text-xs text-[#6B6A68] truncate">{u.name}</span>}
                    </span>
                    <span className="text-xs text-[#6B6A68] shrink-0">
                      {u.comp_status === "active" ? "has a comp" : `${u.silo_count ?? 0} silo(s)`}
                    </span>
                  </button>
                ))}
                {pickRows.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-[#6B6A68]">
                    {pickBusy ? "Searching…" : "No one matches that."}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revoke confirmation modal */}
      {revokeTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             data-testid="revoke-modal-backdrop" onClick={() => setRevokeTarget(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b hairline">
              <h3 className="font-serif-display text-lg">Revoke comp</h3>
              <button type="button" onClick={() => setRevokeTarget(null)} className="p-1 text-[#6B6A68] hover:text-[#1C1B1A]">
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="text-sm text-[#1C1B1A]">
                Revoke free access for <span className="font-medium">{revokeTarget.user_email}</span>?
              </div>
              <div className="text-xs text-[#6B6A68]">
                The user&apos;s account stays active. Creator Pro features will be locked and a &ldquo;set up your monthly subscription&rdquo; alert will appear on their dashboard. If they pay, their originally-gifted SKUs come back automatically.
              </div>
              <textarea value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)}
                        data-testid="revoke-reason"
                        rows={2} placeholder="Revoke reason (required)"
                        className="w-full px-3 py-2 border hairline rounded-md text-sm" />
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-3 border-t hairline bg-[#FAFAF9]">
              <button type="button" onClick={() => setRevokeTarget(null)}
                      className="px-4 py-1.5 rounded-md border hairline text-sm text-[#6B6A68]">
                Cancel
              </button>
              <button type="button" onClick={revoke} disabled={!revokeReason.trim()}
                      data-testid="revoke-submit"
                      className="px-4 py-1.5 rounded-md bg-[#C16E5A] text-white text-sm hover:bg-[#A85A47] disabled:opacity-40 disabled:cursor-not-allowed">
                Revoke
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
